import {
  Autocomplete,
  AutocompleteOption,
  Button,
  IconButton,
  Input,
  ListItemContent,
  ListItemDecorator,
} from '@mui/joy';
import { getIn, useFormikContext } from 'formik';
import { useEffect, useMemo } from 'react';
import { sumBy } from 'lodash';
import { useTranslation } from 'react-i18next';

import {
  BilledStatus,
  DivisionType,
  InboundHoaInvoiceVatCode,
  Indexation,
  Unity,
} from '~/common/enums';
import {
  type ContractOption,
  type ContractPerformanceOption,
  type HomeownerAssociationAccountOption,
  type HomeownerAssociationOption,
} from '@/types/trpc/hoa';
import { type PerformanceFormFields } from './usePerformanceFormikConfig';

import {
  useAutocompleteEnum,
  useAutocompleteOptions,
  useBookYearAutocomplete,
  useHoaAccountAutocomplete,
} from '@/shared/hooks/autocomplete';
import useToggle, { type UseToggle } from '@/shared/hooks/UseToggle';
import { trpc } from '@/config/trpc';
import useDayjs from '@/shared/hooks/useDayjs';

import {
  BuildingIcon,
  CancelIcon,
  CashRegisterIcon,
  DivisionKeyIcon,
  FileSignatureIcon,
  IndividualDivisionIcon,
  InlineSpinner,
  ReceiptIcon,
  ValidIcon,
} from '@/shared/icons/Icons';
import CurrencyFormatAdapter from '@/shared/components/2.0/adapters/CurrencyFormatAdapter';
import FormField from '@/shared/components/2.0/input/FormField';
import FormikDatePicker from '@/shared/components/2.0/formik/FormikDatePicker';
import IndividualDivisionModal from '@/containers/Finance/Shared/modals/IndividualDivisionModal/IndividualDivisionModal';
import KpcTooltip from '@/shared/components/Tooltips/KpcTooltip';
import NumericFormatAdapter from '@/shared/components/2.0/adapters/NumericFormatAdapter';

type Props<T> = {
  isEdit: boolean;
  isMutating: boolean;
  toggle: UseToggle<T>;
  readOnly: boolean;
  disableHoaSelection?: boolean;
};

const AddEditPerformanceFormBody = <T,>({
  isEdit,
  isMutating,
  toggle,
  readOnly,
  disableHoaSelection = false,
}: Props<T>) => {
  const { dayjs } = useDayjs();
  const { initialValues, setFieldValue, setFieldTouched, values, errors, touched } =
    useFormikContext<PerformanceFormFields>();
  const { t } = useTranslation();

  const individualDivisionToggle = useToggle();

  const contractOptionsRes = trpc.hoa.contract.autocomplete.useQuery(
    { homeownerAssociationId: values.homeownerAssociationId as string },
    { enabled: !!values.homeownerAssociationId },
  );

  const hoaOptionsRes = trpc.hoa.autocomplete.useQuery();
  const hoaDivisionKeyOptionsRes = trpc.hoa.divisionKey.autocomplete.useQuery(
    {
      allowDeferredTypes: true,
      id: values.homeownerAssociationId as string,
    },
    { enabled: !!values.homeownerAssociationId },
  );

  const isSameContractPerformance =
    values.contractId === initialValues.contractId &&
    values.contractPerformanceId === initialValues.contractPerformanceId &&
    values.description === initialValues.description &&
    values.total === initialValues.total &&
    values.unity === initialValues.unity &&
    values.unitPrice === initialValues.unitPrice;

  const resetFields = () => {
    setFieldValue('unitPrice', null);
    setFieldValue('unity', Unity.Piece);
    setFieldValue('vat', InboundHoaInvoiceVatCode.PurchaseServices21);
    setFieldValue('description', null);
    setFieldValue('homeownerAssociationGeneralLedgerAccountId', null);
  };

  const handleContractSelection = (option: ContractOption) => {
    setFieldValue('contractId', option.value);

    if (!isSameContractPerformance) {
      setFieldValue('contractPerformanceId', null);
      resetFields();
    }
  };

  const selectedContract = useMemo(
    () => contractOptionsRes.data?.find((contract) => contract.value === values.contractId),
    [contractOptionsRes.data, values.contractId],
  );

  const handleContractPerformanceSelection = (option: ContractPerformanceOption) => {
    setFieldValue('contractPerformanceId', option.value);

    if (!option.teamPerformance) return;

    const { unity, vat, description, price, homeownerAssociationGeneralLedgerAccountId } = option;

    if (!selectedContract) return;

    const {
      contractIndexation: { startIndex, indexationType, indexationPeriods },
    } = selectedContract.data;

    const currentIndexNumber = indexationPeriods?.find((period) =>
      dayjs().isBetween(period.indexationEnd, period.indexationStart),
    )?.index;

    const unitPrice = (() => {
      if (
        !(indexationType ?? []).includes(Indexation.Performances) ||
        !currentIndexNumber ||
        !startIndex
      )
        return price;

      return (price / startIndex) * currentIndexNumber;
    })();

    setFieldValue('unitPrice', unitPrice);
    setFieldValue('unity', unity);
    setFieldValue('vat', vat);
    setFieldValue('description', description);
    setFieldValue(
      'homeownerAssociationGeneralLedgerAccountId',
      homeownerAssociationGeneralLedgerAccountId ?? null,
    );
  };

  const handleHoaSelection = (option: HomeownerAssociationOption) => {
    const selectedId = option.value;

    if (selectedId !== values.homeownerAssociationId) {
      setFieldValue('divisionKeyId', null);
      setFieldValue('homeownerAssociationGeneralLedgerAccountId', null);
      setFieldValue('individualDivisions', []);
      setFieldValue('contractPerformanceId', null);
    }

    setFieldValue('homeownerAssociationId', selectedId);
  };

  const autocompleteProps = {
    bookYear: useBookYearAutocomplete({
      activeOnly: !isEdit,
      callback: (option) => setFieldValue('bookYearId', option.value),
      disableClearable: true,
      homeownerAssociationId: values.homeownerAssociationId,
      selected: values.bookYearId,
    }),
    contract: useAutocompleteOptions({
      ...contractOptionsRes,
      callback: handleContractSelection,
      disableClearable: true,
      selected: values.contractId,
    }),
    contractPerformance: useAutocompleteOptions({
      callback: handleContractPerformanceSelection,
      data: selectedContract?.data.teamPerformances ?? [],
      disableClearable: true,
      selected: values.contractPerformanceId,
    }),
    hoa: useAutocompleteOptions({
      ...hoaOptionsRes,
      callback: handleHoaSelection,
      disableClearable: true,
      selected: values.homeownerAssociationId,
    }),
    hoaAccount: useHoaAccountAutocomplete({
      callback: (option: HomeownerAssociationAccountOption | null) => {
        setFieldValue('homeownerAssociationGeneralLedgerAccountId', option?.value || null);

        if (option?.data.divisionKeyId) {
          setFieldValue('divisionKeyId', option.data.divisionKeyId);
        }
      },
      homeownerAssociationId: values.homeownerAssociationId,
      includeDivisionKeyDescription: true,
      selected: values.homeownerAssociationGeneralLedgerAccountId,
    }),
    hoaDivisionKey: useAutocompleteOptions({
      ...hoaDivisionKeyOptionsRes,
      callback: (option) => setFieldValue('divisionKeyId', option?.value || null),
      enforceSingleOptionPrefill: false,
      selected: values.divisionKeyId,
    }),
    status: useAutocompleteEnum({
      callback: (option) => setFieldValue('status', option.value),
      disableClearable: true,
      selected: values.status,
      srcEnum: BilledStatus,
    }),
    unit: useAutocompleteEnum({
      callback: (option) => setFieldValue('unity', option.value),
      disableClearable: true,
      selected: values.unity,
      srcEnum: Unity,
    }),
    vat: useAutocompleteEnum({
      callback: (option) => setFieldValue('vat', option.value),
      disableClearable: true,
      selected: values.vat,
      srcEnum: InboundHoaInvoiceVatCode,
      translationKey: '_inboundHoaInvoiceVatCode',
    }),
  };

  const showRelationsMissingWarning =
    !values.divisionKeyId &&
    !!values.individualDivisions?.some((division) => !division.buildingRelationId);

  const isIndividualDivisionValid = useMemo(() => {
    if (showRelationsMissingWarning) return false;

    const totalPercentage =
      values.individualDivisions?.reduce(
        (acc, share) => acc + parseFloat(share.percentage ?? '0'),
        0,
      ) ?? 0;

    return Math.abs(totalPercentage - 100) <= 0.01;
  }, [showRelationsMissingWarning, values.individualDivisions]);

  const showIndividualDivisionWarning = useMemo(() => {
    if (
      !!values.divisionKeyId ||
      !values.individualDivisions?.length ||
      values.divisionType !== DivisionType.Amount
    ) {
      return false;
    }
    return sumBy(values.individualDivisions, (d) => d.amount) !== values.total;
  }, [values]);

  const handleBlur = (key: keyof typeof values) => setFieldTouched(key);

  const getError = (key: keyof PerformanceFormFields) =>
    getIn(touched, key) ? getIn(errors, key) : '';

  useEffect(() => {
    const newTotal = values.unitPrice ? values.amount * values.unitPrice : 0;

    if (newTotal !== values.total) {
      setFieldValue('total', values.unitPrice ? values.amount * values.unitPrice : 0);
    }
  }, [values.amount, values.total, values.unitPrice, setFieldValue]);

  return (
    <div className="flex flex-col gap-2">
      <FormField
        required
        error={getError('homeownerAssociationId')}
        label={t('homeownerAssociation')}
      >
        <Autocomplete
          {...autocompleteProps.hoa}
          disabled={autocompleteProps.hoa.disabled || isEdit || readOnly || disableHoaSelection}
          startDecorator={autocompleteProps.hoa.loading ? <InlineSpinner /> : <BuildingIcon />}
          onBlur={() => handleBlur('homeownerAssociationId')}
        />
      </FormField>
      <FormField required error={getError('bookYearId')} label={t('bookyear')}>
        <Autocomplete
          {...autocompleteProps.bookYear}
          disabled={readOnly}
          startDecorator={autocompleteProps.bookYear.loading ? <InlineSpinner /> : <BuildingIcon />}
          onBlur={() => handleBlur('bookYearId')}
        />
      </FormField>
      <FormField required error={getError('contractId')} label={t('contract')}>
        <Autocomplete
          {...autocompleteProps.contract}
          disabled={readOnly}
          startDecorator={
            autocompleteProps.contract.loading ? <InlineSpinner /> : <FileSignatureIcon />
          }
          onBlur={() => handleBlur('contractId')}
        />
      </FormField>
      <FormField required error={getError('contractPerformanceId')} label={t('performance')}>
        <Autocomplete
          {...autocompleteProps.contractPerformance}
          disabled={readOnly}
          startDecorator={
            autocompleteProps.contractPerformance.loading ? <InlineSpinner /> : <CashRegisterIcon />
          }
          onBlur={() => handleBlur('contractPerformanceId')}
        />
      </FormField>
      <div className="flex gap-1">
        <FormField required className="flex-grow" error={getError('amount')} label={t('quantity')}>
          <Input
            disabled={readOnly}
            slotProps={{
              input: {
                component: NumericFormatAdapter,
              },
            }}
            value={values.amount}
            onBlur={() => handleBlur('amount')}
            onChange={(e) => setFieldValue('amount', parseFloat(e.target.value))}
          />
        </FormField>
        <FormField
          required
          className="w-1/6"
          error={getError('unity')}
          label={t('_performanceType.columns.unity')}
        >
          <Autocomplete
            {...autocompleteProps.unit}
            disabled={readOnly}
            onBlur={() => handleBlur('unity')}
          />
        </FormField>
        <FormField required className="w-1/6" error={getError('unitPrice')} label={t('unitPrice')}>
          <Input
            disabled={readOnly}
            slotProps={{
              input: {
                component: CurrencyFormatAdapter,
              },
            }}
            value={values.unitPrice || 0}
            onBlur={() => handleBlur('unitPrice')}
            onChange={(e) => setFieldValue('unitPrice', parseFloat(e.target.value))}
          />
        </FormField>
        <FormField className="w-1/6" label={t('total')}>
          <Input
            readOnly
            disabled={readOnly}
            slotProps={{
              input: {
                component: CurrencyFormatAdapter,
              },
            }}
            value={values.total || 0}
          />
        </FormField>
        <FormField required className="w-1/6" error={getError('vat')} label={t('vat%')}>
          <Autocomplete
            {...autocompleteProps.vat}
            disabled={readOnly}
            onBlur={() => handleBlur('vat')}
          />
        </FormField>

        <FormikDatePicker
          required
          className="w-1/6"
          disabled={readOnly}
          label={t('date')}
          name="date"
        />
      </div>
      <FormField required error={getError('description')} label={t('description')}>
        <Input
          disabled={readOnly}
          value={values.description || ''}
          onBlur={() => handleBlur('description')}
          onChange={(e) => setFieldValue('description', e.target.value)}
        />
      </FormField>
      <FormField
        error={getError('homeownerAssociationGeneralLedgerAccountId')}
        label={t('_generalLedgerAccount.title')}
      >
        <Autocomplete
          {...autocompleteProps.hoaAccount}
          disabled={readOnly}
          getOptionLabel={(option) => `${option.label} ${option.data.divisionKeyDescription ?? ''}`}
          renderOption={(props, option) => (
            <AutocompleteOption {...props}>
              <ListItemContent>
                {option.label}
                {option.data.divisionKeyDescription && (
                  <span className="text-xs italic text-gray-500">
                    {' '}
                    {option.data.divisionKeyDescription}
                  </span>
                )}
              </ListItemContent>
            </AutocompleteOption>
          )}
          startDecorator={
            autocompleteProps.hoaAccount.loading ? <InlineSpinner /> : <ReceiptIcon />
          }
          onBlur={() => handleBlur('homeownerAssociationGeneralLedgerAccountId')}
        />
      </FormField>
      <div className="flex items-end gap-2">
        <FormField className="flex-grow" error={getError('divisionKeyId')} label={t('divisionKey')}>
          <Autocomplete
            {...autocompleteProps.hoaDivisionKey}
            disabled={
              autocompleteProps.hoaDivisionKey.disabled || isIndividualDivisionValid || readOnly
            }
            renderOption={(props, option) => (
              <AutocompleteOption {...props}>
                <ListItemDecorator>
                  <DivisionKeyIcon />
                </ListItemDecorator>
                <ListItemContent>{option.label}</ListItemContent>
              </AutocompleteOption>
            )}
            startDecorator={
              autocompleteProps.hoaDivisionKey.loading ? <InlineSpinner /> : <DivisionKeyIcon />
            }
            onBlur={() => handleBlur('divisionKeyId')}
          />
        </FormField>
        <KpcTooltip title={t('individualDivision')}>
          <IconButton
            color={isIndividualDivisionValid ? 'primary' : 'danger'}
            disabled={!!values.divisionKeyId}
            onClick={() => individualDivisionToggle.show()}
          >
            <IndividualDivisionIcon />
          </IconButton>
        </KpcTooltip>
      </div>

      {showIndividualDivisionWarning && (
        <span className="text-sm text-red-500">
          {t('individualAmountsByAmountMustEqualLineError')}
        </span>
      )}
      {showRelationsMissingWarning && (
        <span className="text-sm text-red-500">{t('errors:relationsMissing')}</span>
      )}

      {isEdit && (
        <FormField required error={getError('status')} label={t('status')}>
          <Autocomplete
            {...autocompleteProps.status}
            disabled={readOnly}
            onBlur={() => handleBlur('status')}
          />
        </FormField>
      )}

      <div className="flex justify-end gap-2 pb-4 pt-2">
        <Button
          color="neutral"
          disabled={isMutating}
          size="sm"
          variant="outlined"
          onClick={toggle.hide}
        >
          <CancelIcon className="pr-2" /> {t('buttons.cancel')}
        </Button>
        <Button
          disabled={isMutating || readOnly}
          startDecorator={isMutating ? <InlineSpinner /> : <ValidIcon />}
          type="submit"
        >
          {t('buttons.save')}
        </Button>
      </div>
      {individualDivisionToggle.value && (
        <IndividualDivisionModal
          bookYearId={values.bookYearId}
          divisionTypeName="divisionType"
          homeownerAssociationId={values.homeownerAssociationId}
          readOnly={readOnly}
          sharesName="individualDivisions"
          toggle={individualDivisionToggle}
          totalAmount={values.total || 0}
        />
      )}
    </div>
  );
};

export default AddEditPerformanceFormBody;
